<template>
  <div class="rich-editor" :class="{ 'rich-editor--focused': isFocused }">
    <!-- Toolbar -->
    <div class="rich-editor__toolbar" role="toolbar" aria-label="Editor de text">
      <button type="button" class="rich-editor__tool" :class="{ 'rich-editor__tool--active': editor && editor.isActive('bold') }" title="Îngroșat" @click="editor && editor.chain().focus().toggleBold().run()">
        <strong>B</strong>
      </button>
      <button type="button" class="rich-editor__tool" :class="{ 'rich-editor__tool--active': editor && editor.isActive('italic') }" title="Italic" @click="editor && editor.chain().focus().toggleItalic().run()">
        <em>I</em>
      </button>
      <button type="button" class="rich-editor__tool" :class="{ 'rich-editor__tool--active': editor && editor.isActive('strike') }" title="Tăiat" @click="editor && editor.chain().focus().toggleStrike().run()">
        <s>S</s>
      </button>

      <span class="rich-editor__tool-sep"></span>

      <!-- Color picker -->
      <label class="rich-editor__tool rich-editor__tool--color" title="Culoare text">
        <span class="rich-editor__color-icon" :style="{ borderBottomColor: currentColor }">A</span>
        <input type="color" class="rich-editor__color-input" :value="currentColor" @input="setColor($event.target.value)" />
      </label>

      <button type="button" class="rich-editor__tool" title="Elimină culoare" @click="editor && editor.chain().focus().unsetColor().run()">
        <i class="mdi mdi-close" aria-hidden="true"></i>
      </button>

      <span class="rich-editor__tool-sep"></span>

      <!-- Image URL -->
      <button type="button" class="rich-editor__tool" title="Inserează imagine (URL)" @click="insertImage">
        <i class="mdi mdi-image" aria-hidden="true"></i>
      </button>

      <!-- YouTube -->
      <button type="button" class="rich-editor__tool" title="Inserează video YouTube" @click="insertYoutube">
        <i class="mdi mdi-play" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Editor content area -->
    <editor-content :editor="editor" class="rich-editor__content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Image } from '@tiptap/extension-image';
import { Youtube } from '@tiptap/extension-youtube';

export default {
  name: 'RichTextEditor',

  components: { EditorContent },

  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      editor: null,
      isFocused: false,
      currentColor: '#212529',
    };
  },

  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({
          // Keep only the extensions we want; disable heading levels we don't need
          heading: { levels: [2, 3] },
        }),
        TextStyle,
        Color,
        Image.configure({ inline: false, allowBase64: false }),
        Youtube.configure({ width: '100%', height: 315, nocookie: true }),
      ],
      content: this.modelValue,
      onUpdate: ({ editor }) => {
        this.$emit('update:modelValue', editor.getHTML());
      },
      onFocus: () => { this.isFocused = true; },
      onBlur: () => { this.isFocused = false; },
      onSelectionUpdate: ({ editor }) => {
        const attrs = editor.getAttributes('textStyle');
        this.currentColor = attrs.color || '#212529';
      },
    });
  },

  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
  },

  watch: {
    modelValue(newVal) {
      if (this.editor && this.editor.getHTML() !== newVal) {
        this.editor.commands.setContent(newVal, false);
      }
    },
  },

  methods: {
    focus() {
      if (this.editor) {
        this.editor.chain().focus().run();
      }
    },

    setColor(color) {
      this.currentColor = color;
      if (this.editor) {
        this.editor.chain().focus().setColor(color).run();
      }
    },

    insertImage() {
      const url = window.prompt('URL-ul imaginii:');
      if (!url || !this.isValidUrl(url)) return;
      if (this.editor) {
        this.editor.chain().focus().setImage({ src: url }).run();
      }
    },

    insertYoutube() {
      const url = window.prompt('URL-ul video-ului YouTube:');
      if (!url || !this.isYoutubeUrl(url)) {
        if (url) window.alert('Introduceți un URL YouTube valid.');
        return;
      }
      if (this.editor) {
        this.editor.commands.setYoutubeVideo({ src: url });
      }
    },

    isValidUrl(url) {
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch {
        return false;
      }
    },

    isYoutubeUrl(url) {
      try {
        const parsed = new URL(url);
        return parsed.hostname === 'www.youtube.com' ||
          parsed.hostname === 'youtube.com' ||
          parsed.hostname === 'youtu.be';
      } catch {
        return false;
      }
    },
  },
};
</script>
